import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Bot, Download, FileText, ShoppingCart, LogOut } from 'lucide-react';
import { portalJson, downloadWithAuth, clearClientToken } from './portalApi';
import { StatusBadge } from '../inbox/StatusBadge';

const KIND_LABEL = { quote: 'Quick quote', questionnaire: 'Quote request', order: 'Order' };
const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '');

const Card = ({ title, icon: Icon, children, testId }) => (
  <section data-testid={testId} style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '1.75rem' }}>
    <h2 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0 0 1.25rem', fontSize: '1.2rem' }}><Icon size={20} color="var(--brand-primary)" /> {title}</h2>
    {children}
  </section>
);

export const PortalDashboard = ({ user, onSignOut }) => {
  const [requests, setRequests] = useState([]);
  const [library, setLibrary] = useState({ owned: [], has_ai_access: false });
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([portalJson('/api/portal/me/requests'), portalJson('/api/portal/library'), portalJson('/api/portal/products')])
      .then(([r, l, p]) => { setRequests(r); setLibrary(l); setProducts(p); })
      .catch((e) => setError(e.message));
  }, []);

  const buy = async (slug) => {
    setBusy(slug); setError('');
    try {
      const data = await portalJson('/api/payments/checkout', { method: 'POST', body: JSON.stringify({ lookup_key: slug, origin_url: window.location.origin }) });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message); setBusy('');
    }
  };

  const ownedSlugs = new Set(library.owned.map((p) => p.slug));
  const forSale = products.filter((p) => !ownedSlugs.has(p.slug));

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="heading-1" data-testid="portal-welcome" style={{ margin: 0, fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}>Welcome back, {user.name.split(' ')[0]}</h1>
          <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '0.3rem 0 0' }}>{user.email}</p>
        </div>
        <button type="button" data-testid="portal-signout" onClick={() => { clearClientToken(); onSignOut(); }} className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem' }}><LogOut size={16} /> Sign out</button>
      </div>
      {error && <p data-testid="portal-error" role="alert" style={{ color: '#c53030', margin: 0 }}>{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
        <Card title="AI Document Assistant" icon={Bot} testId="portal-assistant-card">
          {library.has_ai_access ? (
            <>
              <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Ask about California service of process, filing steps, forms and deadlines - any time, no phone call.</p>
              <Link to="/portal/assistant" className="btn-primary" data-testid="portal-open-assistant" style={{ textDecoration: 'none', display: 'inline-flex' }}>Open the assistant</Link>
            </>
          ) : (
            <>
              <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Unlock unlimited plain-English answers about process serving, court filing and self-help procedures. One-time purchase, lifetime access.</p>
              {products.filter((p) => p.slug === 'ai_assistant_access').map((p) => (
                <button key={p.slug} type="button" className="btn-primary" data-testid="portal-buy-ai_assistant_access" disabled={busy === p.slug} onClick={() => buy(p.slug)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ShoppingCart size={16} /> {busy === p.slug ? 'Redirecting...' : `Unlock for ${money(p.price_cents)}`}
                </button>
              ))}
            </>
          )}
        </Card>

        <Card title="My requests" icon={FileText} testId="portal-requests-card">
          {requests.length === 0 && <p className="body-small" data-testid="portal-requests-empty" style={{ color: 'var(--text-secondary)', margin: 0 }}>No quotes or orders under {user.email} yet. <Link to="/quote" style={{ color: 'var(--brand-primary)' }}>Start a quote</Link>.</p>}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
            {requests.map((r) => (
              <li key={`${r.kind}-${r.id}`} data-testid={`portal-request-${r.id}`} style={{ padding: '0.9rem 1rem', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-page)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>#{r.id} · {r.service_label}</strong><StatusBadge status={r.status} />
                </div>
                <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '0.3rem 0 0' }}>{KIND_LABEL[r.kind]} · {fmt(r.created_at)}{r.quote ? ` · Quoted $${r.quote.amount.toFixed(2)}` : ''}</p>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="My library" icon={BookOpen} testId="portal-library-card">
          {library.owned.filter((p) => p.kind === 'ebook').length === 0 && <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>Guides you purchase will appear here for download.</p>}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
            {library.owned.filter((p) => p.kind === 'ebook').map((p) => (
              <li key={p.slug} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontWeight: '600' }}>{p.title}</span>
                <button type="button" data-testid={`portal-download-${p.slug}`} onClick={() => downloadWithAuth(`/api/portal/library/${p.slug}/download`, p.filename || `${p.slug}.pdf`)} className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem' }}><Download size={14} /> PDF</button>
              </li>
            ))}
          </ul>
        </Card>

        {forSale.filter((p) => p.kind === 'ebook').length > 0 && (
          <Card title="Legal guides & eBooks" icon={ShoppingCart} testId="portal-guides-card">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
              {forSale.filter((p) => p.kind === 'ebook').map((p) => (
                <li key={p.slug} style={{ padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                  <strong>{p.title}</strong>
                  {p.description && <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '0.3rem 0 0.75rem' }}>{p.description}</p>}
                  <button type="button" className="btn-primary" data-testid={`portal-buy-${p.slug}`} disabled={busy === p.slug} onClick={() => buy(p.slug)} style={{ padding: '0.6rem 1.1rem' }}>{busy === p.slug ? 'Redirecting...' : `Buy for ${money(p.price_cents)}`}</button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
};
